import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api } from '../api/client'
import type { CompareChannel, Project, SeriesResponse } from '../api/types'
import { ChartFrame } from '../components/ChartFrame'
import { TimeSeriesChart } from '../components/TimeSeriesChart'
import { fetchAnalysisSeries } from '../lib/analysisSources'
import {
  FORMULA_TEMPLATES, calculatedSensorStore, calculatedSourceId, calculationSourceBindings, compileFormula,
  createCalculatedSensorId, deleteCalculatedSensor, saveCalculatedSensor, validateCalculatedSensor,
  type CalculatedDefinition, type CalculatedSensor, type InputAlias,
} from '../lib/calculatedSensors'
import { MONITORING_MATH_LIBRARY, type ExpressionMode } from '../lib/monitoringExpressions'
import { useRemote } from '../lib/remoteStore'
import { formatDateTime } from '../lib/time'
import { useAsync } from '../lib/useAsync'
import './calculated-sensors.css'

interface Draft {
  id: string
  name: string
  formula: string
  mode: ExpressionMode
  unit: string
  quantity: string
  precision: number
  inputs: Record<InputAlias, string>
  /** The project it is saved in, which decides who sees and changes it. */
  project_id: number | null
}
function emptyDraft(projectId: number | null = null): Draft {
  return { id: createCalculatedSensorId(), name: '', formula: 'A - B', mode: 'expression', unit: 'm',
    quantity: 'difference', precision: 3, inputs: { A: '', B: '', C: '' }, project_id: projectId }
}
function draftFor(sensor: CalculatedSensor): Draft {
  return { id: sensor.id, name: sensor.name, formula: sensor.formula, mode: sensor.mode ?? 'expression', unit: sensor.unit,
    quantity: sensor.axis_group, precision: sensor.precision, project_id: sensor.project_id,
    inputs: { A: '', B: '', C: '', ...Object.fromEntries(sensor.inputs.map(input => [input.alias, input.sourceId])) } }
}
function definitionFor(draft: Draft): CalculatedDefinition {
  const used = compileFormula(draft.formula, { mode: draft.mode }).variables
  return { id: draft.id, name: draft.name.trim(), formula: draft.formula.trim(),
    ...(draft.mode === 'program' ? { mode: draft.mode } : {}),
    unit: draft.unit.trim(), axis_group: draft.quantity.trim(), precision: draft.precision,
    inputs: used.map(alias => ({ alias, sourceId: draft.inputs[alias] })) }
}
const failure = (cause: unknown, fallback: string) => cause instanceof Error ? cause.message : fallback
function unusedCopyName(name: string, items: CalculatedSensor[]): string {
  const names = new Set(items.map(item => item.name.toLocaleLowerCase()))
  let next = name.slice(0, 90) + ' copy', index = 2
  while (names.has(next.toLocaleLowerCase())) next = name.slice(0, 85) + ' copy ' + index++
  return next
}

export function CalculatedSensors() {
  const [params, setParams] = useSearchParams()
  const store = useRemote(calculatedSensorStore)
  const items = store.items
  const [notice, setNotice] = useState('')
  useEffect(() => { if (store.error) setNotice(store.error) }, [store.error])
  // The projects a calculation can be saved in: the reader's as an Editor
  // or Admin (every one for a platform admin).
  const projects = useAsync(() => api.projects.list().then(data => data.projects
    .filter(project => project.my_role === 'editor' || project.my_role === 'admin')), [])
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState<Draft>(() => items[0] ? draftFor(items[0]) : emptyDraft())
  const [busy, setBusy] = useState(false)
  // The first saved calculation once the list has come, if nothing was opened.
  const opened = useRef(items.length > 0)
  useEffect(() => {
    if (opened.current || !store.loaded) return
    opened.current = true
    if (items[0] && !dirty) setDraft(draftFor(items[0]))
    // Once, when the list first arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.loaded])
  useEffect(() => {
    const only = projects.data?.length === 1 ? projects.data[0]!.id : null
    if (only !== null) setDraft(current => current.project_id === null ? { ...current, project_id: only } : current)
  }, [projects.data])
  const [error, setError] = useState('')
  const [dirty, setDirty] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<CalculatedSensor | null>(null)
  const [preview, setPreview] = useState<{ data?: SeriesResponse; loading: boolean; error?: string; range?: string }>({ loading: false })
  const [period, setPeriod] = useState('24h')
  const [library, setLibrary] = useState<'functions' | 'channels' | 'code' | null>(null)
  const formulaField = useRef<HTMLTextAreaElement>(null)
  const generation = useRef(0)
  const channels = useAsync(() => api.compareChannels(), [])
  const saved = items.find(item => item.id === draft.id)
  const existing = !!saved
  // Members see a project's calculations; its Editors and Admins change them.
  const readOnly = !!saved && !saved.can_edit
  const byId = useMemo(() => new Map((channels.data ?? []).map(channel => [channel.station + '|' + channel.parameter, channel])), [channels.data])
  const groups = useMemo(() => {
    const result = new Map<string, CompareChannel[]>()
    for (const channel of channels.data ?? []) {
      const group = result.get(channel.station_name) ?? []
      group.push(channel); result.set(channel.station_name, group)
    }
    return [...result].sort(([a], [b]) => a.localeCompare(b))
  }, [channels.data])
  const syntax = useMemo(() => {
    try {
      const parsed = compileFormula(draft.formula, { mode: draft.mode })
      return { variables: parsed.variables, directReferences: parsed.directReferences, error: '' }
    } catch (cause) { return { variables: [] as InputAlias[], directReferences: [] as string[], error: cause instanceof Error ? cause.message : 'Invalid formula.' } }
  }, [draft.formula, draft.mode])
  const matching = items.filter(item => (item.name + ' ' + item.formula + ' ' + item.unit).toLocaleLowerCase().includes(query.toLocaleLowerCase()))

  useEffect(() => {
    if (params.get('new') !== '1') return
    open()
    const next = new URLSearchParams(params)
    next.delete('new')
    setParams(next, { replace: true })
  }, [params, setParams])

  // A preview still running when the page goes must not land.
  useEffect(() => () => { generation.current += 1 }, [])

  function update<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft(current => ({ ...current, [key]: value })); setDirty(true); setError('')
    generation.current += 1
    setPreview({ loading: false })
  }
  function open(item?: CalculatedSensor) {
    generation.current += 1
    setDraft(item ? draftFor(item) : emptyDraft(projects.data?.length === 1 ? projects.data[0]!.id : null)); setDirty(false)
    setError(''); setPreview({ loading: false }); setNotice('')
  }
  function replaceSource(formula: string, mode: ExpressionMode = draft.mode) {
    setDraft(current => ({ ...current, formula, mode })); setDirty(true); setError('')
    generation.current++; setPreview({ loading: false })
  }
  function changeMode(mode: ExpressionMode) {
    if (mode === draft.mode) return
    const singleReturn = /^\s*return\s+([^;]+);?\s*$/i.exec(draft.formula)
    replaceSource(mode === 'program' ? 'return ' + draft.formula.trim().replace(/^=\s*/, '') + ';' : singleReturn?.[1]?.trim() ?? draft.formula, mode)
  }
  function useExample(formula: string, code = false, output?: { unit: string; quantity: string }) {
    replaceSource(code ? formula : draft.mode === 'program' ? 'return ' + formula + ';' : formula, code ? 'program' : draft.mode)
    if (output) setDraft(current => ({ ...current, unit: output.unit, quantity: output.quantity }))
    setLibrary(null)
  }
  function insertChannel(sourceId: string) {
    const field = formulaField.current, start = field?.selectionStart ?? draft.formula.length, end = field?.selectionEnd ?? start
    const snippet = '[' + sourceId + ']'
    update('formula', draft.formula.slice(0, start) + snippet + draft.formula.slice(end))
    setLibrary(null)
  }
  function validDefinition(): CalculatedDefinition | null {
    try {
      const definition = definitionFor(draft)
      const invalid = validateCalculatedSensor(definition)
      if (invalid) { setError(invalid); return null }
      const absent = calculationSourceBindings(definition).find(input => !byId.has(input.sourceId))
      if (absent) { setError('Source ' + absent.alias + ' is unavailable. Select an available sensor or channel reference.'); return null }
      if (items.some(item => item.id !== draft.id && item.project_id === draft.project_id
          && item.name.toLocaleLowerCase() === definition.name.toLocaleLowerCase())) {
        setError('A calculated sensor with this name already exists in this project.'); return null
      }
      return definition
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Check this calculation.'); return null }
  }
  async function save(event: FormEvent) {
    event.preventDefault()
    const definition = validDefinition()
    if (!definition) return
    if (draft.project_id === null) { setError('Choose a project.'); return }
    setBusy(true)
    try {
      const stored = await saveCalculatedSensor(definition, draft.project_id, existing ? draft.id : undefined)
      setDraft(draftFor(stored)); setDirty(false); setError(''); setNotice(stored.name + ' saved.')
    } catch (cause) { setError(failure(cause, 'Could not save the calculated sensor.')) } finally { setBusy(false) }
  }
  async function duplicate() {
    const definition = validDefinition()
    if (!definition || draft.project_id === null) return
    setBusy(true)
    try {
      const copy = await saveCalculatedSensor({ ...definition, name: unusedCopyName(definition.name, items) }, draft.project_id)
      open(copy); setNotice(copy.name + ' created.')
    } catch (cause) { setError(failure(cause, 'Could not save the copy.')) } finally { setBusy(false) }
  }
  async function remove() {
    if (!confirmDelete) return
    try {
      await deleteCalculatedSensor(confirmDelete.id)
      setConfirmDelete(null); open(calculatedSensorStore.snapshot().items[0]); setNotice('Calculated sensor removed.')
    } catch (cause) { setError(failure(cause, 'Could not remove the calculated sensor.')); setConfirmDelete(null) }
  }
  async function runPreview() {
    const definition = validDefinition()
    if (!definition) return
    const mine = ++generation.current
    const to = new Date(), from = new Date(to.getTime() - (period === '7d' ? 7 : 1) * 86_400_000)
    setError(''); setPreview({ loading: true })
    try {
      const data = await fetchAnalysisSeries([calculatedSourceId(definition.id)], from, to, [definition])
      if (mine === generation.current) setPreview({ data, loading: false, range: formatDateTime(from) + ' to ' + formatDateTime(to) })
    } catch (cause) {
      if (mine === generation.current) setPreview({ loading: false, error: cause instanceof Error ? cause.message : 'Preview failed.' })
    }
  }
  const output = preview.data?.series[0]
  const outputValues = output?.points.filter(point => point[1] !== null) ?? []
  const latest = outputValues[outputValues.length - 1]

  return <section className="calc-page" aria-labelledby="calculated-sensors-title">
    <header className="calc-heading">
      <h1 id="calculated-sensors-title" className="sr-only">Calculated Sensors</h1>
      {(projects.data?.length ?? 0) > 0 && <button className="button primary" onClick={() => open()}>New calculated sensor</button>}
    </header>
    {notice && <div className="calc-notice" role="status">{notice}<button type="button" aria-label="Dismiss notice" onClick={() => setNotice('')}>×</button></div>}
    <div className="calc-layout">
      <aside className="calc-library" aria-label="Calculated sensor library">
        <div className="calc-library-head"><strong>Calculations</strong><span>{items.length}</span></div>
        <input type="search" aria-label="Search calculated sensors" placeholder="Search calculations" value={query} onChange={event => setQuery(event.target.value)} />
        <div className="calc-library-list">
          {matching.map(item => <button key={item.id} className={'calc-library-item' + (draft.id === item.id ? ' is-active' : '')}
            onClick={() => open(item)} aria-pressed={draft.id === item.id}>
            <span>{item.name}</span><code>{item.formula}</code><small>{item.unit} · {item.mode === 'program' ? 'Code' : 'Formula'} · {calculationSourceBindings(item).length} inputs · {item.project}</small>
          </button>)}
          {!matching.length && <div className="calc-empty">{!store.loaded ? 'Loading calculations…' : items.length ? 'No matching calculations.'
            : projects.data?.length ? 'Create your first calculated sensor.' : "A project's Editors and Admins create calculated sensors."}</div>}
        </div>
        <div className="calc-library-footer">Shared with each project's members</div>
      </aside>
      <div className="calc-workspace">
        <form className="calc-editor" onSubmit={save}>
          <div className="calc-editor-head"><h2>{existing ? draft.name || 'Edit calculation' : 'New calculation'}</h2>
            <span className="calc-badge">{dirty ? 'Unsaved changes' : 'Derived data'}</span></div>
          {readOnly && <p className="calc-readonly" role="note">A project Editor or Admin changes this calculation; you can preview it and use it in analyses.</p>}
          <div className="calc-field"><label htmlFor="calc-name">Sensor name</label>
            <input id="calc-name" required maxLength={100} value={draft.name} onChange={event => update('name', event.target.value)} placeholder="e.g. Reservoir to piezometer head difference" /></div>
          <div className="calc-field"><label htmlFor="calc-project">Project</label>
            <select id="calc-project" required value={draft.project_id ?? ''} disabled={readOnly}
              onChange={event => update('project_id', event.target.value ? Number(event.target.value) : null)}>
              <option value="" disabled>Choose a project</option>
              {projectChoices(projects.data ?? [], saved).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <small>Its members see it; its Editors and Admins change it. A manual sensor it reads must be in the same project.</small></div>
          <div className="calc-templates" aria-label="Formula templates">
            {FORMULA_TEMPLATES.map(template => <button type="button" key={template.name}
              className={draft.formula === template.formula ? 'is-active' : ''} onClick={() => useExample(template.formula)}>{template.name}</button>)}
          </div>
          <div className="calc-formula-toolbar">
            <div className="calc-mode" aria-label="Calculation editor mode">
              <button type="button" aria-pressed={draft.mode === 'expression'} onClick={() => changeMode('expression')}>Formula</button>
              <button type="button" aria-pressed={draft.mode === 'program'} onClick={() => changeMode('program')}>Code</button>
            </div>
            <div className="calc-formula-tools"><button type="button" onClick={() => setLibrary('channels')}>Insert channel</button>
              <button type="button" onClick={() => setLibrary('functions')}>Math library</button></div>
          </div>
          <div className="calc-field"><label htmlFor="calc-formula">{draft.mode === 'program' ? 'Calculation code' : 'Formula'}</label>
            <textarea ref={formulaField} id="calc-formula" className={'calc-formula' + (draft.mode === 'program' ? ' is-code' : '')}
              required maxLength={draft.mode === 'program' ? 4000 : 500} rows={draft.mode === 'program' ? 6 : 2} spellCheck={false} autoComplete="off"
              value={draft.formula} onChange={event => update('formula', event.target.value)} aria-describedby="calc-syntax" aria-invalid={!!syntax.error} />
            <small id="calc-syntax" className={syntax.error ? 'calc-syntax-error' : 'calc-syntax-ready'} role="status">{syntax.error || 'Valid numeric ' + (draft.mode === 'program' ? 'program' : 'formula') + ' · ' + (syntax.variables.length + syntax.directReferences.length) + ' input references'}</small>
            {draft.mode === 'program' && <small>Use <code>let name = expression;</code> and a final <code>return expression;</code>. <button type="button" className="calc-text-button" onClick={() => setLibrary('code')}>Code examples</button></small>}
          </div>
          <div className="calc-inputs">
            {syntax.variables.map(alias => {
              const selected = byId.get(draft.inputs[alias])
              return <div className="calc-input-row" key={alias}>
                <span className="calc-alias">{alias}</span>
                <label className="calc-field" htmlFor={'calc-input-' + alias}><span>Input {alias}
                  {selected && <small className="calc-source-station">{selected.station_name}</small>}</span>
                  <select id={'calc-input-' + alias} value={draft.inputs[alias]} required
                    onChange={event => update('inputs', { ...draft.inputs, [alias]: event.target.value })}>
                    <option value="">Select a source sensor</option>
                    {draft.inputs[alias] && !selected && <option value={draft.inputs[alias]} disabled>Unavailable source</option>}
                    {groups.map(([station, grouped]) => <optgroup key={station} label={station}>
                      {grouped.map(channel => <option key={channel.station + '|' + channel.parameter} value={channel.station + '|' + channel.parameter}>{channel.display_name} ({channel.unit})</option>)}
                    </optgroup>)}
                  </select>
                </label>
                <span className="calc-input-unit">{selected?.unit ?? ''}</span>
              </div>
            })}
          </div>
          {!!syntax.directReferences.length && <div className="calc-channel-references" aria-label="Referenced channels">
            <strong>Referenced channels</strong>
            {syntax.directReferences.map(id => { const channel = byId.get(id); return <div key={id} className={channel ? '' : 'is-unavailable'}>
              <code>[{id}]</code><span>{channel ? channel.station_name + ' · ' + channel.display_name + ' (' + channel.unit + ')' : 'Unavailable channel'}</span>
            </div> })}
          </div>}
          {channels.loading && <p className="muted small">Loading source sensors…</p>}
          {channels.error && <p className="error" role="alert">{channels.error}</p>}
          <div className="calc-output-fields">
            <div className="calc-field"><label htmlFor="calc-unit">Output unit</label>
              <input id="calc-unit" required maxLength={30} value={draft.unit} onChange={event => update('unit', event.target.value)} placeholder="m" /></div>
            <div className="calc-field"><label htmlFor="calc-quantity">Output quantity</label>
              <input id="calc-quantity" required list="calc-quantities" maxLength={80} value={draft.quantity} onChange={event => update('quantity', event.target.value)} placeholder="head_difference" />
              <datalist id="calc-quantities">{['difference', 'displacement', 'head_difference', 'pressure', 'level', 'depth', 'discharge', 'temperature', 'velocity', 'tilt', 'ratio'].map(quantity => <option key={quantity} value={quantity} />)}</datalist>
            </div>
            <div className="calc-field"><label htmlFor="calc-precision">Decimal places</label>
              <select id="calc-precision" value={draft.precision} onChange={event => update('precision', Number(event.target.value))}>
                {Array.from({ length: 10 }, (_, index) => <option key={index} value={index}>{index}</option>)}
              </select></div>
          </div>
          <details className="calc-method"><summary>Calculation method</summary>
            <p>Inputs match at the same timestamp. Missing inputs and invalid mathematics produce gaps. Input QC flags are retained. Units describe the result; they do not convert values.</p>
            <p>Math functions combine values at each timestamp, not across a time window. Trigonometric functions use radians. Use RADIANS or DEGREES for conversion. PI and E are constants.</p>
            <p>Raw observations remain unchanged. Check the physical meaning of formulas involving counters, bearings or different units.</p>
          </details>
          {error && <p className="error" role="alert">{error}</p>}
          <div className="calc-actions">
            {existing && !readOnly && <><button className="button" type="button" onClick={() => void duplicate()} disabled={busy}>Duplicate</button>
              <button className="button calc-delete" type="button" onClick={() => setConfirmDelete(saved ?? null)}>Delete</button></>}
            <div className="calc-actions-main"><button className="button" type="button" onClick={runPreview} disabled={channels.loading || preview.loading}>Preview calculation</button>
              {!readOnly && <button className="button primary" type="submit" disabled={channels.loading || busy}>Save calculated sensor</button>}</div>
          </div>
        </form>
        <section className="calc-preview" aria-labelledby="calc-preview-title">
          <div className="calc-preview-head"><div><h2 id="calc-preview-title">Preview</h2>
            {preview.range && <span>{preview.range} · Tbilisi</span>}</div>
            <div className="calc-preview-tools"><select aria-label="Calculation preview period" value={period} onChange={event => { setPeriod(event.target.value); generation.current++; setPreview({ loading: false }) }}>
              <option value="24h">Last 24 hours</option><option value="7d">Last 7 days</option></select>
              {existing && <Link className="button" to={'/analysis/new?s=' + encodeURIComponent(calculatedSourceId(draft.id))}>Use in analysis</Link>}</div>
          </div>
          {!preview.loading && !preview.data && !preview.error ? <div className="calc-preview-empty">
            <svg width="56" height="40" viewBox="0 0 56 40" aria-hidden="true"><path d="M3 34H53M3 4V34M7 26L17 21L28 24L39 12L50 7" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
            <strong>Preview your calculation</strong><span>Select inputs and run the preview.</span></div>
            : <ChartFrame height={310} loading={preview.loading} error={preview.error} empty={!outputValues.length} emptyMessage="No matching non-null observations in this period.">
              {output && <><div className="calc-preview-summary"><strong>{latest?.[1]?.toFixed(draft.precision)} <span>{draft.unit}</span></strong>
                <span>{outputValues.length.toLocaleString()} calculated values</span><span>{output.points.length - outputValues.length} gaps</span></div>
                <TimeSeriesChart series={[{ ...output, axis_group: draft.quantity.replace(/_/g, ' ') }]} height={310} /></>}
            </ChartFrame>}
          {preview.data?.truncated && <p className="error" role="alert">{preview.data.truncated_at
            ? 'Preview ends at ' + formatDateTime(preview.data.truncated_at) + '. Narrow the window to show all values.'
            : 'This preview is incomplete. Narrow the time window.'}</p>}
        </section>
      </div>
    </div>
    {confirmDelete && <DeleteCalculationDialog item={confirmDelete} onCancel={() => setConfirmDelete(null)} onDelete={() => void remove()} />}
    {library && <MathLibraryDialog initialTab={library} channels={channels.data ?? []} onClose={() => setLibrary(null)}
      onExample={useExample} onChannel={insertChannel} />}
  </section>
}

/** The projects a calculation can go in, and its own project whatever the
 *  reader's role there, so the select can name it. */
function projectChoices(projects: Project[], saved: CalculatedSensor | undefined): { id: number; name: string }[] {
  return saved && !projects.some(project => project.id === saved.project_id)
    ? [{ id: saved.project_id, name: saved.project }, ...projects] : projects
}

const CODE_EXAMPLES: { name: string; description: string; source: string; output?: { unit: string; quantity: string } }[] = [
  { name: 'Hydrostatic pressure', description: 'Head difference in metres converted to kPa using water density 1,000 kg/m³ and g = 9.80665 m/s². Sets the output unit to kPa.', source: 'let head = A - B;\nlet pressure = head * 9.80665;\nreturn pressure;', output: { unit: 'kPa', quantity: 'pressure' } },
  { name: 'Calibration and offset', description: 'Keep the calibration factor and zero offset visible as named values.', source: 'let factor = 0.001;\nlet offset = 0.25;\nreturn A * factor + offset;' },
  { name: 'Planar displacement', description: 'Resultant from two perpendicular displacement components in the same unit.', source: 'let dx = A;\nlet dy = B;\nreturn HYPOT(dx, dy);' },
]
function MathLibraryDialog({ initialTab, channels, onClose, onExample, onChannel }: {
  initialTab: 'functions' | 'channels' | 'code'; channels: CompareChannel[]; onClose: () => void
  onExample: (formula: string, code?: boolean, output?: { unit: string; quantity: string }) => void; onChannel: (id: string) => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [tab, setTab] = useState(initialTab), [query, setQuery] = useState(''), [category, setCategory] = useState('All')
  useEffect(() => {
    const focus = document.activeElement as HTMLElement | null
    dialog.current?.showModal()
    return () => { focus?.focus() }
  }, [])
  const search = query.trim().toLocaleLowerCase()
  const functions = MONITORING_MATH_LIBRARY.filter(item => (category === 'All' || item.category === category)
    && (item.name + ' ' + item.description + ' ' + item.signature).toLocaleLowerCase().includes(search))
  const selectedChannels = channels.filter(channel => (channel.station_name + ' ' + channel.station + ' ' + channel.parameter + ' ' + channel.display_name).toLocaleLowerCase().includes(search))
  return <dialog ref={dialog} className="calc-math-dialog" aria-labelledby="calc-math-title" onCancel={event => { event.preventDefault(); onClose() }}>
    <header><div><div className="calc-eyebrow">Calculation tools</div><h2 id="calc-math-title">Math library</h2></div>
      <button type="button" className="calc-math-close" onClick={onClose} aria-label="Close math library">×</button></header>
    <div className="calc-math-tabs" aria-label="Math library section">
      {(['functions', 'channels', 'code'] as const).map(item => <button type="button" key={item} aria-pressed={tab === item}
        onClick={() => { setTab(item); setQuery('') }}>{item === 'functions' ? 'Functions' : item === 'channels' ? 'Channels' : 'Code examples'}</button>)}
    </div>
    {tab !== 'code' && <div className="calc-math-filters"><input type="search" aria-label={tab === 'functions' ? 'Search math functions' : 'Search channel references'}
      placeholder={tab === 'functions' ? 'Search by function or calculation' : 'Search station, channel name or code'} value={query} onChange={event => setQuery(event.target.value)} />
      {tab === 'functions' && <select aria-label="Math category" value={category} onChange={event => setCategory(event.target.value)}>
        {['All', 'Arithmetic', 'Statistics', 'Angles', 'Logic'].map(item => <option key={item}>{item}</option>)}</select>}</div>}
    <div className="calc-math-body">
      {tab === 'functions' && <>
        {functions.map(item => {
          const example = ['AND', 'OR', 'NOT'].includes(item.name) ? 'IF(' + item.example + ', 1, 0)' : item.example
          return <article className="calc-function-card" key={item.name}><div><code>{item.signature}</code><span>{item.category}</span></div>
            <p>{item.description}</p><div className="calc-function-example"><code>{example}</code><button type="button" onClick={() => onExample(example)}>Use example</button></div></article>
        })}
        {!functions.length && <p className="calc-math-empty">No matching functions.</p>}
      </>}
      {tab === 'channels' && <>
        <p className="calc-math-help">Select text in the editor, then insert a channel to replace it. The reference uses the stored station and channel names.</p>
        {selectedChannels.map(channel => <button type="button" className="calc-reference-option" key={channel.station + '|' + channel.parameter}
          onClick={() => onChannel(channel.station + '|' + channel.parameter)}>
          <span><strong>{channel.display_name}</strong><small>{channel.station_name} · {channel.unit}</small></span><code>[{channel.station}|{channel.parameter}]</code>
        </button>)}
        {!selectedChannels.length && <p className="calc-math-empty">No matching channels.</p>}
      </>}
      {tab === 'code' && <>
        <p className="calc-math-help">Define up to 12 local values with <code>let</code>, then return a numeric result. Each assignment ends with a semicolon.</p>
        {CODE_EXAMPLES.map(item => <article className="calc-function-card" key={item.name}><h3>{item.name}</h3><p>{item.description}</p>
          <pre>{item.source}</pre><button type="button" onClick={() => onExample(item.source, true, item.output)}>Use code example</button></article>)}
      </>}
    </div>
    <footer>Functions use simultaneous values. Source observations stay unchanged.</footer>
  </dialog>
}

function DeleteCalculationDialog({ item, onCancel, onDelete }: { item: CalculatedSensor; onCancel: () => void; onDelete: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null)
  useEffect(() => { dialog.current?.showModal() }, [])
  return <dialog ref={dialog} className="calc-delete-dialog" aria-labelledby="calc-delete-title" onCancel={event => { event.preventDefault(); onCancel() }}>
    <h2 id="calc-delete-title">Delete calculated sensor?</h2>
    <p>{item.name}</p><p className="muted small">It leaves {item.project}'s calculations. Saved analyses using it will need another source. Raw observations are kept.</p>
    <div className="calc-dialog-actions"><button className="button" onClick={onCancel}>Cancel</button>
      <button className="button primary" onClick={onDelete}>Delete calculation</button></div>
  </dialog>
}
